import { IconButton, TableCell, TableRow, useEnqueueBbSnackbar } from '@bb-ui/react-library';
import { Start } from '@bb-ui/icons/dist/medium';
import * as React from 'react';
import { SnackbarKey } from 'notistack';
import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { CreateExecutionRequestBody, JobExecution, IJobSummary } from '@lct-jobs/core';
import { JobSubmitEvent, RunDialog } from './RunDialog';
import useRestApi from '../../hooks/useRestApi';
import { useSnackbar } from '../../hooks/useSnackbar';
import { useJobRegion } from 'contexts/JobRegionContext';

export interface JobRowProps {
  jobSummary: IJobSummary;
  tenantId: string;
  siteTenantId?: string;
}

export const JobRow: React.FC<JobRowProps> = (props) => {
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [snackbarKey, setSnackbarKey] = React.useState<SnackbarKey | null>(null);
  let { tenantId } = props;
  if (props.siteTenantId && props.jobSummary.targetTenantType[0] === 'LearnInstance') {
    tenantId = props.siteTenantId;
  }

  const { lctApiUrl } = useJobRegion();

  const jobApi = useRestApi<JobExecution>(lctApiUrl(`tenants/${tenantId}/jobManager/executions`), {
    manual: true,
  });
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const history = useHistory();

  const dismissSnackbar = useCallback(() => {
    if (snackbarKey) {
      closeSnackbar(snackbarKey);
      setSnackbarKey(null);
    }
  }, [closeSnackbar, snackbarKey]);

  const submitJob = (s: JobSubmitEvent) => {
    setDialogOpen(false);
    setSnackbarKey(enqueueSnackbar('Submitting Execution...', { variant: 'info' }));

    const body: CreateExecutionRequestBody = {
      jobInput: s.formData,
      jobId: props.jobSummary.jobId,
      providerId: s.selectedProvider,
      scheduledStartTime: s.scheduledStartTime,
    };

    jobApi.doPost(body);
  };

  React.useEffect(() => {
    if (jobApi.failedRequests.length > 0) {
      jobApi.clearFailedRequests();
      dismissSnackbar();
      setSnackbarKey(enqueueSnackbar('Error submitting execution', { variant: 'error' }));
    }
  }, [closeSnackbar, dismissSnackbar, enqueueSnackbar, jobApi, jobApi.failedRequests]);

  const showSuccessSnackbar = useEnqueueBbSnackbar(
    { snackbarMessage: 'Execution Submitted' },
    {
      actionMessage: 'View Details',
      onActionClick: () => {
        const result = jobApi.succeededRequests.at(0);
        const executionId: string | undefined = result?.response?.data?.executionId;
        if (tenantId && executionId) {
          if (tenantId !== 'global') {
            history.push(`/tenants/${props.tenantId}/executions/${executionId}`);
            closeSnackbar();
          } else {
            history.push(`/jobs/executions/${executionId}`);
            closeSnackbar();
          }
        }
      },
      closeButtonAriaLabel: 'close',
    },
  );

  React.useEffect(() => {
    if (jobApi.succeededRequests.length > 0) {
      dismissSnackbar();
      showSuccessSnackbar();
      jobApi.clearSucceededRequests();
    }
  }, [dismissSnackbar, jobApi, showSuccessSnackbar]);

  return (
    <TableRow key={props.jobSummary.jobId}>
      <TableCell>{props.jobSummary.jobName}</TableCell>
      <TableCell>{props.jobSummary.jobDomain}</TableCell>
      <TableCell>{props.jobSummary.jobDescription}</TableCell>
      <TableCell>
        <IconButton onClick={() => setDialogOpen(true)}>
          <Start />
          {dialogOpen && (
            <RunDialog
              jobSummary={props.jobSummary}
              tenantId={tenantId}
              closeDialog={() => setDialogOpen(false)}
              submitJob={submitJob}
            />
          )}
        </IconButton>
      </TableCell>
    </TableRow>
  );
};
